import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Languages } from "lucide-react";
import { Logo } from "@/components/common/Brand";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangMenu } from "@/components/common/LangMenu";
import { CentralClock } from "@/components/common/CentralClock";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function ModLogin() {
  const { login, apiErr } = useAuth();
  const { tr, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(apiErr(err?.response?.data?.detail) || tr.mLoginErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-zinc-950 px-4">
      <div className="absolute left-1/2 top-4 -translate-x-1/2">
        <CentralClock />
      </div>
      <div className="absolute right-4 top-4">
        <LangMenu />
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <Logo className="h-14" />
          <h1 className="mt-5 text-lg font-semibold tracking-tight text-zinc-50">{tr.mLogin}</h1>
          <p className="mt-1 text-[13px] text-zinc-500">{tr.mLoginSub}</p>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-zinc-800/70 bg-[#111113] p-6" data-testid="mod-login-form">
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mEmail}</Label>
              <Input
                data-testid="mod-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@dsa-specialists.eu"
                className="border-zinc-800 bg-zinc-950 text-[14px]"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mPassword}</Label>
              <Input
                data-testid="mod-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="border-zinc-800 bg-zinc-950 text-[14px]"
              />
            </div>
            {error && (
              <div data-testid="mod-login-error" className="rounded-md border border-red-600/40 bg-red-600/10 px-3 py-2 text-[13px] text-red-300">
                {error}
              </div>
            )}
            <Button
              data-testid="mod-login-btn"
              type="submit"
              disabled={loading}
              className="h-10 w-full bg-blue-600 text-[14px] text-white hover:bg-blue-700"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : tr.mSignIn}
              {loading ? tr.mSigningIn : ""}
            </Button>
          </div>
        </form>

        <button
          onClick={() => navigate("/")}
          className="mt-4 flex w-full items-center justify-center gap-1.5 text-[13px] text-zinc-500 hover:text-zinc-300"
        >
          <ArrowLeft size={14} /> {tr.mBackToSite}
        </button>
      </div>
    </div>
  );
}
